import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ILink, NcgLocationMapCategory, NgcApiLocation } from '@ncg/data';

import { expansionAnimation } from '../../../expansion-panel/expansion.animation';
import { DEFAULT_LAT, ICoordinations } from '../map-container.component';

declare let google: any;

@Component({
    selector: 'ncg-map-location-info',
    template: `
        <ng-container *ngIf="location">
            <div class="columns is-gapless is-multiline is-mobile">
                <div class="column map-item__icon icon map-dealer-icon">
                    <img [src]="markerIconUrl" width="36px" alt="" *ngIf="markerIconUrl" />
                </div>
                <div class="column">
                    <div class="columns is-gapless is-multiline">
                        <div class="column">
                            <div class="map-item__content">
                                <h5
                                    class="map-item__content--name"
                                    *ngIf="location.name"
                                    [ngStyle]="{ 'margin-bottom': !location.distributor?.name ? '0.4rem' : '0' }">
                                    {{ location.name }}
                                </h5>
                                <h6
                                    class="map-item__content--name map-item__content--name--official"
                                    *ngIf="!isBrandLayout && location.distributor?.name">
                                    {{ location.distributor?.name }}
                                </h6>
                                <ng-container *ngIf="location.address">
                                    <address
                                        class="map-item__content--address"
                                        *ngIf="location.address?.street || location.address?.zipcode || location.address?.city">
                                        <div *ngIf="location.address?.street">{{ location.address?.street }}</div>
                                        <span *ngIf="location.address?.zipcode">{{ location.address?.zipcode }}</span>
                                        <span *ngIf="location.address?.city"> {{ location.address?.city }}</span>
                                    </address>
                                </ng-container>

                                <div [ngClass]="isBrandLayout ? '' : 'is-hidden-tablet is-hidden-desktop'">
                                    <a
                                        class="map-item__direction-link map-item__direction-link--inline"
                                        [href]="addDirectionsUrl()"
                                        target="_blank"
                                        rel="noopener">
                                        {{ 'map.directions_label_short' | translate }}
                                    </a>
                                </div>

                                <button *ngIf="!expanded" type="button" class="button is-link map-item__more-button" (click)="onShowMoreClicked()">
                                    {{ 'map.show_more' | translate }}
                                </button>
                            </div>
                        </div>

                        <ng-container *ngIf="!isBrandLayout">
                            <div class="column is-narrow is-hidden-mobile">
                                <a class="map-item__direction-link" [href]="addDirectionsUrl()" target="_blank" rel="noopener">{{
                                    'map.directions_label_short' | translate
                                }}</a>
                            </div>
                        </ng-container>

                        <ng-container *ngIf="isBrandLayout">
                            <div class="column is-4 map-item__brands">
                                <div class="columns is-multiline is-mobile is-variable is-1 is-vcentered">
                                    <div class="column is-narrow map-item__brand-image" *ngFor="let brand of location.brands">
                                        <ncg-logos
                                            [allowAllLogos]="true"
                                            [key]="brand"
                                            iconWidthSm="18"
                                            iconWidth="21"
                                            iconWidthLg="30"
                                            iconWidthXl="35"></ncg-logos>
                                    </div>
                                </div>
                            </div>
                        </ng-container>
                    </div>
                </div>
            </div>
            <div class="map-item__info expansion-panel__content" [@expandAnimation]="expandedState">
                <div class="map-item__box">
                    <p>
                        <span class="map-phone__title">{{ 'map.phone_label' | translate }} </span>
                        <a class="map-phone__number" [href]="'tel:' + location.phone">{{ location.phone }}</a>
                    </p>
                    <p *ngIf="location.email">
                        <a [href]="'mailto:' + location.email">{{ location.email }}</a>
                    </p>
                    <p *ngIf="location.externalDealerSite">
                        <a [href]="location.externalDealerSite" [attr.title]="location.externalDealerSite" target="_blank">
                            <ncg-text-sprite-icon [iconUrl]="'assets/images/sprite.svg#external-link'">{{
                                'map.dealer_link' | translate
                            }}</ncg-text-sprite-icon>
                        </a>
                    </p>
                </div>
                <ncg-map-location-info-contact
                    [location]="location"
                    [currentCategory]="currentCategory"
                    [serviceLink]="serviceLink"
                    [testDriveLink]="testDriveLink"
                    [hideServiceButton]="hideServiceButton"
                    [hideTestDriveButton]="hideTestDriveButton"
                    [contactLink]="contactLink"
                    [hideContactButton]="hideContactButton"
                    [isBrandLayout]="isBrandLayout"
                    [openingHoursLink]="openingHoursLink"></ncg-map-location-info-contact>

                <button *ngIf="expanded" type="button" class="button is-link" (click)="onShowMoreClicked()">{{ 'map.show_less' | translate }}</button>
            </div>
        </ng-container>
    `,
    animations: [expansionAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MapLocationInfoComponent implements OnInit {
    @Input()
    public location?: NgcApiLocation;
    @Input()
    public userCurrentPosition?: ICoordinations;
    @Input()
    public serviceLink?: ILink;
    @Input()
    public testDriveLink?: ILink;
    @Input()
    public hideServiceButton?: boolean;
    @Input()
    public hideTestDriveButton?: boolean;
    @Input()
    public contactLink?: ILink;
    @Input()
    public hideContactButton?: boolean;
    @Input()
    public markerIconUrl: string;
    @Input()
    public currentCategory?: NcgLocationMapCategory;
    @Input()
    public isBrandLayout?: boolean;
    @Input()
    public openingHoursLink?: ILink;

    public expanded = false;
    public expandedState?: 'expanded' | 'closed';

    public ngOnInit() {
        this.expandedState = this.expanded ? 'expanded' : 'closed';
    }

    public onShowMoreClicked(event?: Event) {
        this.expanded = !this.expanded;
        this.expandedState = this.expanded ? 'expanded' : 'closed';

        if (event) {
            // Prevent double-click
            event.stopPropagation();
            event.preventDefault();
        }
    }

    public addDirectionsUrl() {
        if (this.location) {
            const origin =
                this.userCurrentPosition && this.userCurrentPosition.lat !== DEFAULT_LAT
                    ? `${this.userCurrentPosition.lat},${this.userCurrentPosition.lng}`
                    : '';
            const destination = `${this.location?.address?.lat},${this.location?.address?.lng}`;
            const travelmode = google.maps.TravelMode.DRIVING;
            const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=${travelmode}`;
            return encodeURI(url);
        }
    }
}
